import {createElement, MouseEvent, useState} from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  PopoverOrigin,
  SvgIconTypeMap,
  SxProps,
  Typography,
} from '@mui/material';
import {MoreVert} from '@mui/icons-material';
import {IconType} from 'react-icons/lib';
import {OverridableComponent} from '@mui/material/OverridableComponent';
import {Pallete} from '../../theme/bluebird';
import {Tooltip} from '../tooltip/tooltip';

type ListPositionType = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export type ComboActionsType = Array<{
  label: string;
  icon?: {
    element:
      | IconType
      | (OverridableComponent<SvgIconTypeMap<{}, 'svg'>> & {
          muiName: string;
        });
    size?: string;
  };
  disabled?: boolean;
  disabledTooltip?: string;
  callback: () => void;
}>;

export interface ComboActionsProps {
  actions: ComboActionsType;
  className?: string;
  listPosition?: ListPositionType;
  wrapperSX?: SxProps;
  triggerSX?: SxProps;
}

export const ComboActions = ({
  actions,
  className,
  wrapperSX,
  triggerSX,
  listPosition = 'top-right',
}: ComboActionsProps) => {
  const [optionsGroupMenuAnchor, setOptionsGroupMenuAnchor] = useState<null | HTMLElement>(null);

  const handleOptionsGroupMenuClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setOptionsGroupMenuAnchor(event.currentTarget);
  };

  const handleOptionsGroupMenuClose = () => setOptionsGroupMenuAnchor(null);

  const optionsGroupMenuOpen = Boolean(optionsGroupMenuAnchor);

  const handleOptionClick = (event: MouseEvent, callback: () => void) => {
    event.stopPropagation();
    handleOptionsGroupMenuClose();
    callback();
  };

  const handleListPosition = (
    position: ListPositionType
  ): {transformOrigin: PopoverOrigin | undefined; anchorOrigin: PopoverOrigin | undefined} => {
    switch (position) {
      case 'top-left':
        return {
          transformOrigin: {horizontal: 'right', vertical: 'top'},
          anchorOrigin: {horizontal: 'right', vertical: 'bottom'},
        };
      case 'top-right':
        return {
          transformOrigin: {horizontal: 'right', vertical: 'top'},
          anchorOrigin: {horizontal: 'right', vertical: 'bottom'},
        };
      case 'bottom-left':
        return {
          transformOrigin: {horizontal: 'right', vertical: 'top'},
          anchorOrigin: {horizontal: 'right', vertical: 'bottom'},
        };
      case 'bottom-right':
        return {
          transformOrigin: {horizontal: 'right', vertical: 'top'},
          anchorOrigin: {horizontal: 'right', vertical: 'bottom'},
        };

      default:
        return {
          transformOrigin: {horizontal: 'right', vertical: 'top'},
          anchorOrigin: {horizontal: 'right', vertical: 'bottom'},
        };
    }
  };

  return (
    <Box sx={wrapperSX} className={className}>
      <IconButton
        aria-controls={optionsGroupMenuAnchor ? 'options-menu-right' : undefined}
        aria-haspopup="true"
        aria-expanded={optionsGroupMenuOpen ? 'true' : undefined}
        onClick={handleOptionsGroupMenuClick}
        sx={{
          borderRadius: '5px',
          color: 'currentcolor',
          ...triggerSX,
        }}
      >
        <MoreVert color="inherit" />
      </IconButton>
      <Menu
        id="options-menu-right"
        anchorEl={optionsGroupMenuAnchor}
        open={optionsGroupMenuOpen}
        onClose={handleOptionsGroupMenuClose}
        onClick={(evt: MouseEvent) => evt.stopPropagation()}
        sx={{
          transformOrigin: 'bottom 20px',
          '.MuiList-root': {
            padding: '4px 0',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          },
        }}
        {...handleListPosition(listPosition)}
      >
        {actions.map(({label, icon, disabled, disabledTooltip, callback}) => (
          <Tooltip.NotAllowedIf
            condition={disabled}
            title={disabledTooltip}
            key={`header_${label}_option`}
          >
            <MenuItem
              key={`header_${label}_option`}
              onClick={(evt: MouseEvent) => handleOptionClick(evt, callback)}
              disabled={disabled}
              sx={{
                height: '28px',
                minHeight: 'auto',
                padding: '8px 12px',
                flexDirection: 'row',
                gap: '8px',
                alignItems: 'center',
                color: '#464646',
                '&:focus': {
                  backgroundColor: 'initial',
                },
                '&:hover': {
                  backgroundColor: Pallete.blue.bgseniormenu,
                },
              }}
            >
              <>
                {icon ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      position: 'relative',
                      width: icon.size,
                      height: icon.size,
                    }}
                  >
                    {createElement<{style: {color: string; width: string; height: string}}>(
                      icon.element,
                      {
                        style: {
                          color: 'currentcolor',
                          width: icon.size ?? '16px',
                          height: icon.size ?? '16px',
                        },
                      }
                    )}
                  </Box>
                ) : (
                  <></>
                )}

                <Typography
                  component="label"
                  sx={{
                    color: 'currentColor',
                    fontFamily: 'Roboto',
                    fontSize: '12px',
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  {label}
                </Typography>
              </>
            </MenuItem>
          </Tooltip.NotAllowedIf>
        ))}
      </Menu>
    </Box>
  );
};
